import {useCallback, useMemo, useState} from "react";

import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    useAnswerCheckedListener,
    useExerciseQuestionAnswerableStateChangedListener,
} from "@khan/events-guide";
import {
    PopoverClosedReason,
    type Notification,
} from "@khan/guide-notifications";
import {GuideStateApi} from "@khan/guide-state";

import {NotificationActionBubblesContext} from "./context.ts";
import {useDeferDisplayedActions} from "./use-defer-displayed-actions.ts";

interface Props {
    children: React.ReactNode;
    /**
     * The name of the event that triggered the notification.
     */
    eventName: string;
    /**
     * The initial state of the notification action bubbles.
     */
    initialState?: {isCollapsed?: boolean};
    /**
     * The notification associated with the state.
     */
    notification: Notification;
    /**
     * Whether the user prefers reduced motion.
     */
    prefersReducedMotion?: boolean;
}

// Create a context provider component
export const NotificationActionBubblesStateProvider = (
    props: Props,
): React.ReactElement => {
    const {
        children,
        eventName: notificationEventName,
        initialState = {},
        notification,
        prefersReducedMotion = true,
    } = props;

    const actionBubbles = useMemo(
        () => notification.actionBubbles ?? [],
        [notification.actionBubbles],
    );
    const [filteredActions, setFilteredActions] = useState(actionBubbles);
    const {displayedActions, allActionsDisplayed} = useDeferDisplayedActions(
        filteredActions,
        prefersReducedMotion ? 0 : 1000,
    );
    const [isCollapsed, setIsCollapsed] = useState(
        initialState.isCollapsed ?? false,
    );

    // NAB-specific event handling
    useExerciseQuestionAnswerableStateChangedListener(({answerable}) => {
        // This handler is responsible for updating Notification Action Bubbles
        // displayed in response to the "user-entered-answer-to-question" event.
        // If the exercise question has become answerable, we display the
        // "Review my answer" action bubble, otherwise we hide it.

        // This event is only relevant for NABs displayed in response to the
        // "user-entered-answer-to-question" event.
        if (notificationEventName !== "user-entered-answer-to-question") {
            return;
        }

        // This shouldn't ever happen, but we'll guard against it just in case.
        if (actionBubbles.length === 0) {
            return;
        }

        if (answerable) {
            if (actionBubbles.length !== filteredActions.length) {
                setFilteredActions(actionBubbles);
            }
        } else {
            // The "Review my answer" action bubble is always the first action
            // bubble.
            if (actionBubbles[0] === filteredActions[0]) {
                setFilteredActions(actionBubbles.slice(1));
            }
        }
    });

    useAnswerCheckedListener(() => {
        // This handler is responsible for updating Notification Action Bubbles
        // displayed in response to the "user-entered-answer-to-question" event.
        // We want them to dismiss automatically when the user submits their
        // answer.

        // This event is only relevant for NABs displayed in response to the
        // "user-entered-answer-to-question" event.
        if (notificationEventName !== "user-entered-answer-to-question") {
            return;
        }
        notification.onClose(PopoverClosedReason.autoDismissed);
    });

    // NAB handlers
    const handleExpand = useCallback(() => {
        setIsCollapsed(false);
    }, []);

    const handleCollapse = useCallback(() => {
        setIsCollapsed(true);

        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideNotifActionBubblesPutAway",
            eventSchemaVersion: 1,
            dismissalType: "COLLAPSE",
            aiGuideNavigationId: GuideStateApi.latest().navigation.id,
        });
    }, []);

    const handleDismiss = useCallback(() => {
        notification.onClose(PopoverClosedReason.userDismissed);

        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideNotifActionBubblesPutAway",
            eventSchemaVersion: 1,
            dismissalType: "CLOSE_BOX",
            aiGuideNavigationId: GuideStateApi.latest().navigation.id,
        });
    }, [notification]);

    const handleDidShow = useCallback(() => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideNotifActionBubblesDisplayed",
            eventSchemaVersion: 1,
            aiGuideNavigationId: GuideStateApi.latest().navigation.id,
        });
    }, []);

    // Value to be passed to the context
    const context = useMemo(
        () => ({
            state: {
                actions: actionBubbles,
                allActionsDisplayed,
                displayedActions,
                isCollapsed,
            },
            handleCollapse,
            handleDismiss,
            handleExpand,
            handleDidShow,
        }),
        [
            actionBubbles,
            allActionsDisplayed,
            displayedActions,
            isCollapsed,
            handleCollapse,
            handleDidShow,
            handleDismiss,
            handleExpand,
        ],
    );

    return (
        <NotificationActionBubblesContext.Provider value={context}>
            {children}
        </NotificationActionBubblesContext.Provider>
    );
};
